/**
 * 连线判定 —— 从 Playground 抽出的纯逻辑，目的是能被测试。
 *
 * 为什么值得单独成模块：这里有一个**踩过坑的语义区分**。
 *
 * Vue Flow 会把 `isValidConnection` 用在两个完全不同的场景：
 *
 *   ① 校验**拖拽中的候选连线**（"这条线能不能连？"）
 *   ② 校验**每一条已存在的边**（edges 变化时 createGraphEdges 会重跑全部）
 *
 * 场景 ② 决定了：判定函数里**不能**有「这条线是否已经存在」这类条件 ——
 * 对已存在的边来说那永远为 true，会把所有边一次判死
 * （控制台狂刷 EDGE_INVALID，图上的边全部消失）。
 *
 * 所以这里刻意拆成三个函数，语义各自独立：
 *
 *   isStructurallyValid  自连 / 类型 / 环     → 两种场景都成立，可交给 Vue Flow
 *   alreadyConnected     这条候选线是否已存在  → 只在场景 ① 有意义
 *   canLink              结构合法 && 未连接    → 只用于端口高亮/变暗
 */
import type { GraphNodeModel } from '../types/node'
import { canConnect } from '../types/socket'
import { findSocketIn } from '../node/sockets'
import {
  repeaterCountFor,
  repeaterHandle,
  repeaterIndexOf,
} from '../node/rows'
import { hasEdge, wouldCreateCycle } from './connectionRules'
import type { EdgeLike } from './connectionRules'

/*
 * `findSocketIn` 现在住在 `node/sockets.ts`（节点层）—— 资产校验也要用它，
 * 而 `node/` 不能反向 import `graph/`。这里**再导出**一次，
 * 让既有 import 点不用改（而且只有一份实现）。
 */
export { findSocketIn } from '../node/sockets'

export interface LinkCheckInput {
  models: readonly GraphNodeModel[]
  edges: readonly EdgeLike[]
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}

/**
 * **结构合法性**：自连 / 类型 / 环。
 *
 * ⚠️ 这个函数会被 Vue Flow 用来复检**已存在的边**，所以它的判断
 * 必须对「新候选」和「既有边」都成立 —— 不要在这里加「是否已存在」。
 */
export function isStructurallyValid(input: LinkCheckInput): boolean {
  const { models, edges, source, sourceHandle, target, targetHandle } = input

  if (!sourceHandle || !targetHandle) return false
  if (source === target) return false // 自连

  const from = findSocketIn(models, source, sourceHandle)
  const to = findSocketIn(models, target, targetHandle)
  if (!from || !to) return false

  // 传整个 SocketDef：list 会顺带比对 itemType（未声明的一侧视为兼容）
  if (!canConnect(from, to)) return false

  // 环检测：对既有边同样成立（无环图里再走一遍仍是无环）
  if (wouldCreateCycle(edges, source, target)) return false

  return true
}

/**
 * 拖拽时的完整判断：结构合法 **且** 尚未连接。
 *
 * 只用于端口高亮/变暗。**不要交给 Vue Flow 的 isValidConnection** ——
 * 那样已存在的边会全部被判非法并丢弃。
 */
/** 这条候选连线是否已经存在。**只在拖拽候选的语境下有意义。** */
export function alreadyConnected(input: LinkCheckInput): boolean {
  const { edges, source, sourceHandle, target, targetHandle } = input
  return hasEdge(edges, { source, sourceHandle, target, targetHandle })
}

/**
 * 求某个节点上「当前这次拖拽能落到哪些端口」。
 *
 * 方向由 startSide 决定，而且 canLink 的实参顺序要跟着翻转 ——
 * 因为 canLink 的约定始终是「(输出, 输入)」：
 *
 *   startSide = 'source'（从输出起线）→ 找**输入**落点：(起点, 候选)
 *   startSide = 'target'（从输入起线）→ 找**输出**落点：(候选, 起点)
 *
 * 抽成纯函数是为了能单测 —— 之前它埋在 Playground 里，
 * 「拖拽时到底哪些端口该亮」只能靠眼睛看，出问题很难查。
 */
export interface ConnectableQuery {
  models: readonly GraphNodeModel[]
  edges: readonly EdgeLike[]
  /** 起线的节点 */
  startNodeId: string
  /** 起线的 handle */
  startHandleId: string
  /** 起点在哪一侧 */
  startSide: 'source' | 'target'
  /** 要为哪个节点求落点 */
  nodeId: string
}

export function connectableHandles(query: ConnectableQuery): string[] {
  const { models, edges, startNodeId, startHandleId, startSide, nodeId } = query

  const model = models.find((m) => m.id === nodeId)
  if (!model) return []

  const wantInputs = startSide !== 'target'
  const ids: string[] = []

  const ok = (a: string, b: string, c: string, d: string) =>
    canLink({ models, edges, source: a, sourceHandle: b, target: c, targetHandle: d })

  for (const row of model.rows) {
    if (row.kind === 'input') {
      if (wantInputs && ok(startNodeId, startHandleId, nodeId, row.socket.id)) {
        ids.push(row.socket.id)
      }
      continue
    }
    if (row.kind === 'output') {
      // 反向：候选输出在前，起点输入在后
      if (!wantInputs && ok(nodeId, row.socket.id, startNodeId, startHandleId)) {
        ids.push(row.socket.id)
      }
      continue
    }
    /*
     * 变参行：每个可见实例都是合法落点（只有正向成立）。
     *
     * ⚠️ 实例数量从**边**推导，不读 `row.connected` ——
     * 那是编辑器维护的视图状态，headless / 尚未同步时会偏小，
     * 导致「明明还能接的那个实例不亮」。（求值层踩过同一个坑。）
     */
    if (row.kind === 'repeater' && wantInputs) {
      let lastConnected = -1
      for (const edge of edges) {
        if (edge.target !== nodeId) continue
        const at = repeaterIndexOf(row, edge.targetHandle ?? '')
        if (at > lastConnected) lastConnected = at
      }
      const total = repeaterCountFor(row, lastConnected)
      for (let i = 0; i < total; i += 1) {
        const handle = repeaterHandle(row, i)
        if (ok(startNodeId, startHandleId, nodeId, handle)) ids.push(handle)
      }
    }
  }
  return ids
}

export function canLink(input: LinkCheckInput): boolean {
  return isStructurallyValid(input) && !alreadyConnected(input)
}
